import React, { memo } from 'react';
import MuxPlayer from '@mux/mux-player-react';

import '@mux/mux-player/themes/minimal';
import { buildMuxPosterUrl, buildPosterUrl } from '../../utils/videoPoster';
import { getMuxEnvKey } from '../../utils/mux';
import VideoFrame, { PlayerFrame, PlaceholderPicture } from './styles';
import colors from '../../constants/colors';

const DefaultVideoPlayer = ({
  video,
  placeholderPicture,
  autoPlay = false,
  muted = false,
  loop = false,
  playsInline = true,
  onEnded,
  onReady,
  poster,
  controls = false,
  className,
  metadata,
}) => {
  // Build poster URL with explicit priority:
  // 1. Explicit poster prop, so a caller can always override
  // 2. Frame 0 of the video itself. The still and the first decoded frame are
  //    then the same image, so nothing jumps the moment playback starts — a
  //    curated preview picture is a different shot and visibly pops.
  // 3. video.previewPicture, then placeholderPicture (session/program picture).
  //    Only the no-video branch below gets this far.
  const muxPosterUrl = buildMuxPosterUrl(video?.muxPlaybackId);
  const videoPreviewUrl = buildPosterUrl(video?.previewPicture);
  const placeholderUrl = buildPosterUrl(placeholderPicture);
  const posterSource = poster || muxPosterUrl || videoPreviewUrl || placeholderUrl || null;

  const wrapperClassName = ['default-video-player', className].filter(Boolean).join(' ');

  if (!video || !video.muxPlaybackId) {
    return (
      <VideoFrame className={wrapperClassName}>
        <PlaceholderPicture
          className="default-video-placeholder"
          style={posterSource ? { backgroundImage: `url(${posterSource})` } : undefined}
        />
      </VideoFrame>
    );
  }

  return (
    <PlayerFrame className={wrapperClassName}>
      <MuxPlayer
        className="default-video-player__mux"
        theme="minimal"
        accentColor={colors.main.lightBlue}
        playbackId={video.muxPlaybackId}
        poster={posterSource || undefined}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        playsinline={playsInline}
        preload="auto"
        controls={controls}
        onEnded={onEnded}
        onLoadedData={onReady}
        envKey={getMuxEnvKey()}
        metadata={
          metadata || {
            video_id: video.id,
            video_title: video.title || video.originalName,
          }
        }
        streamType="on-demand"
      />
    </PlayerFrame>
  );
};

export default memo(DefaultVideoPlayer);
